// kommunikationTabelle.ts — reine, testbare Logik der Mail-Tabelle im
// Kontakt-Reiter „Kommunikation" (User 02.11.2026: „nicht mehr trennen
// zwischen Posteingang und Archiv", tabellarisch, sortier- und durchsuchbar).
//
// Ein gemeinsamer Bestand aus zwei Quellen:
//   (1) Graph-Suche `participants:` je lesbarem Postfach — findet die Mails
//       des Kontakts in ALLEN Ordnern (Posteingang, Archiv, Gesendet,
//       Unterordner), nicht nur unter den neuesten 200/100 eines Ordners;
//   (2) die beim Kontakt gespeicherten Verknüpfungen (MailLink-Snapshots) —
//       auch für Postfächer, die der Nutzer gar nicht lesen darf.
// Frei von React/DOM, damit Zusammenführen, Suche und Sortierung per Vitest
// geprüft werden können.

import type { Email, MailFolder } from "../services/graphMailService";
import type { MailLinkForContactDto } from "../services/mailLinkService";
import { emailAusMailLink, type MailVorgangRef } from "./kommunikationHelpers";
import { buildParticipantsQuery } from "./mail/mailContactHistory";
import { mailboxChipLabel } from "./mail/mailSearch";
import { zeitImDatumsbereich, type DateRangeValue } from "./redesign/table/filterModel";

export type KommRichtung = "eingang" | "ausgang";

/** Eine Zeile der Mail-Tabelle — unabhängig davon, aus welcher Quelle sie kommt. */
export interface KommMailZeile {
    key: string;
    /** Postfach, in dem die Mail liegt ("" = unbekannt). */
    mailbox: string;
    email: Email;
    link?: MailLinkForContactDto;
    vorgang: MailVorgangRef | null;
    richtung: KommRichtung;
    von: string;
    an: string;
    /** Anzeigename des Ordners; `null` = nur aus der Sicherung bekannt. */
    ordner: string | null;
    /** Zeitpunkt in ms (0 = unbekannt). */
    zeit: number;
}

/** Treffer der Graph-Suche EINES Postfachs. */
export interface PostfachTreffer {
    mailbox: string;
    mails: Email[];
}

/** Adressen je Suchanfrage — lange ODER-Ketten lehnt die Graph-Suche ab. */
export const ADRESSEN_JE_ABFRAGE = 10;

/** Maximal so viele Treffer liefert die Suche je Postfach und Anfrage. */
export const TREFFER_JE_POSTFACH = 100;

/**
 * KQL-Anfragen für die Adressen eines Kontakts: bereinigt, dedupliziert und in
 * Blöcke zu {@link ADRESSEN_JE_ABFRAGE} geteilt (eine Firma mit vielen
 * Ansprechpartnern ergäbe sonst eine Anfrage, die Graph abweist).
 */
export function teilnehmerAbfragen(adressen: string[], jeAbfrage = ADRESSEN_JE_ABFRAGE): string[] {
    const sauber = Array.from(
        new Set((adressen ?? []).map((a) => String(a ?? "").trim().toLowerCase()).filter((a) => a.includes("@"))),
    );
    const out: string[] = [];
    for (let i = 0; i < sauber.length; i += jeAbfrage) {
        const kql = buildParticipantsQuery(sauber.slice(i, i + jeAbfrage));
        if (kql) out.push(kql);
    }
    return out;
}

/**
 * Ordner-Id → Anzeigename je Postfach, INKLUSIVE Posteingang (anders als
 * `buildFolderNameMap`, die den Posteingang für den Such-Chip auslässt).
 */
export function ordnerNamen(folderTrees: Record<string, MailFolder[]>): Map<string, Map<string, string>> {
    const out = new Map<string, Map<string, string>>();
    for (const [mailbox, baum] of Object.entries(folderTrees ?? {})) {
        const map = new Map<string, string>();
        const walk = (nodes: MailFolder[]) => {
            for (const n of nodes ?? []) {
                if (n.id) map.set(n.id, n.wellKnownName === "inbox" ? "Posteingang" : n.displayName);
                if (n.children?.length) walk(n.children);
            }
        };
        walk(baum);
        out.set(mailbox.trim().toLowerCase(), map);
    }
    return out;
}

/**
 * Ordner, die kein Schriftverkehr sind: Entwürfe, Junk und Papierkorb. Die
 * Graph-Suche durchsucht sie mit (den Papierkorb sogar ausdrücklich). Erkannt
 * am Namen (deutsch/englisch), weil das Backend nur den Posteingang als
 * Well-Known markiert.
 */
const NICHT_SCHRIFTVERKEHR = new Set([
    "entwürfe",
    "drafts",
    "junk-e-mail",
    "junk email",
    "junk-email",
    "gelöschte elemente",
    "deleted items",
    "postausgang",
    "outbox",
]);

export function istSchriftverkehrsOrdner(name: string | null | undefined): boolean {
    if (!name) return true; // unbekannt → lieber zeigen
    return !NICHT_SCHRIFTVERKEHR.has(name.trim().toLowerCase());
}

const formatAdresse = (name?: string | null, adresse?: string | null): string => {
    const n = (name ?? "").trim();
    const a = (adresse ?? "").trim();
    if (n && a && n.toLowerCase() !== a.toLowerCase()) return `${n} <${a}>`;
    return n || a || "";
};

const zeitVon = (email: Email): number => {
    const d = email.date instanceof Date ? email.date : new Date(email.date as unknown as string);
    const t = d.getTime();
    return Number.isFinite(t) && t > 0 ? t : 0;
};

/**
 * Richtung aus Sicht des Büros: Schreibt eine Adresse des Kontakts, ist es ein
 * Eingang — sonst hat das Büro geschrieben (Ausgang).
 */
export function richtungVon(email: Pick<Email, "senderEmail">, kontaktAdressen: Set<string>): KommRichtung {
    const absender = (email.senderEmail ?? "").trim().toLowerCase();
    return absender && kontaktAdressen.has(absender) ? "eingang" : "ausgang";
}

const normPostfach = (m: string | null | undefined) => (m ?? "").trim().toLowerCase();

/** Minuten-genauer Fingerabdruck — erkennt einen Snapshot wieder, dessen Mail inzwischen verschoben wurde. */
const fingerabdruck = (mailbox: string, betreff: string, zeit: number) =>
    `${normPostfach(mailbox)}|${(betreff ?? "").trim().toLowerCase()}|${Math.floor(zeit / 60000)}`;

/**
 * Führt Graph-Treffer aller Postfächer und die Verknüpfungen zu EINER Liste
 * zusammen, neueste zuerst.
 *
 * Doppelte fallen weg:
 *  - dieselbe Nachricht (Postfach + Graph-Id);
 *  - dieselbe Mail in zwei Postfächern (gleiche `internetMessageId`) — das
 *    aktuelle Postfach gewinnt, sonst das zuerst gelieferte;
 *  - ein Snapshot, dessen Mail Graph unter neuer Id führt (Graph vergibt beim
 *    Verschieben eine neue Id): erkannt an Postfach + Betreff + Minute. Der
 *    Snapshot reichert dann die Graph-Zeile um Verknüpfung und Vorgang an.
 */
export function fuehreKommunikationZusammen(args: {
    treffer: PostfachTreffer[];
    links: MailLinkForContactDto[] | undefined;
    kontaktAdressen: Set<string>;
    aktuellesPostfach?: string | null;
    ordner?: Map<string, Map<string, string>>;
}): KommMailZeile[] {
    const { treffer, links, kontaktAdressen, aktuellesPostfach, ordner } = args;
    const aktuell = normPostfach(aktuellesPostfach);
    const zeilen = new Map<string, KommMailZeile>();
    const jeInternetId = new Map<string, string>();
    const jeFingerabdruck = new Map<string, string>();

    const baue = (mailbox: string, email: Email, ordnerName: string | null, link?: MailLinkForContactDto): KommMailZeile => {
        // „An" aus den To-Empfängern; fehlen sie (nur Cc/Bcc adressiert, etwa
        // ein Rundschreiben), steht der Cc da — gekennzeichnet, statt leer.
        const to = (email.to ?? []).map((e) => formatAdresse(e.name, e.address)).filter(Boolean).join(", ");
        const cc = (email.cc ?? []).map((e) => formatAdresse(e.name, e.address)).filter(Boolean).join(", ");
        const an = link?.recipients?.trim() || to || (cc ? `Cc: ${cc}` : "");
        return {
            key: `${normPostfach(mailbox)}::${email.id}`,
            mailbox,
            email,
            link,
            vorgang: link?.vorgang ?? null,
            richtung: richtungVon(email, kontaktAdressen),
            von: formatAdresse(email.senderName, email.senderEmail),
            an,
            ordner: ordnerName,
            zeit: zeitVon(email),
        };
    };

    // Aktuelles Postfach zuerst, damit es bei Kopien in mehreren Postfächern gewinnt.
    const sortiert = [...(treffer ?? [])].sort(
        (a, b) => Number(normPostfach(b.mailbox) === aktuell) - Number(normPostfach(a.mailbox) === aktuell),
    );
    for (const { mailbox, mails } of sortiert) {
        const namen = ordner?.get(normPostfach(mailbox));
        for (const email of mails ?? []) {
            const ordnerName = email.parentFolderId ? namen?.get(email.parentFolderId) ?? null : null;
            if (ordnerName && !istSchriftverkehrsOrdner(ordnerName)) continue;
            const zeile = baue(mailbox, email, ordnerName);
            if (zeilen.has(zeile.key)) continue;
            const iid = email.internetMessageId?.trim();
            if (iid && jeInternetId.has(iid)) continue;
            zeilen.set(zeile.key, zeile);
            if (iid) jeInternetId.set(iid, zeile.key);
            jeFingerabdruck.set(fingerabdruck(mailbox, email.subject, zeile.zeit), zeile.key);
        }
    }

    for (const link of links ?? []) {
        const key = `${normPostfach(link.mailbox)}::${link.messageId}`;
        const snapshot = emailAusMailLink(link);
        const iid = link.internetMessageId?.trim();
        const treffend =
            (zeilen.has(key) ? key : undefined) ??
            (iid ? jeInternetId.get(iid) : undefined) ??
            jeFingerabdruck.get(fingerabdruck(link.mailbox, snapshot.subject, zeitVon(snapshot)));
        const vorhanden = treffend ? zeilen.get(treffend) : undefined;
        if (vorhanden) {
            if (!vorhanden.link || (!vorhanden.vorgang && link.vorgang)) {
                zeilen.set(vorhanden.key, {
                    ...vorhanden,
                    link: vorhanden.link ?? link,
                    vorgang: vorhanden.vorgang ?? link.vorgang ?? null,
                    an: vorhanden.an || link.recipients?.trim() || "",
                });
            }
            continue;
        }
        const zeile = baue(link.mailbox, snapshot, null, link);
        zeilen.set(zeile.key, zeile);
        if (iid) jeInternetId.set(iid, zeile.key);
    }

    return Array.from(zeilen.values()).sort((a, b) => b.zeit - a.zeit);
}

// ── Suche ───────────────────────────────────────────────────────────────────

const suchtext = (z: KommMailZeile): string =>
    [
        z.email.subject,
        z.von,
        z.an,
        z.email.senderEmail,
        z.email.preview,
        z.vorgang?.titel,
        z.ordner,
        z.mailbox,
        (z.email.cc ?? []).map((c) => `${c.name ?? ""} ${c.address ?? ""}`).join(" "),
    ]
        .filter(Boolean)
        .join(" \u0001 ")
        .toLowerCase();

/** Zerlegt eine Suche in Wörter; ALLE müssen vorkommen (Reihenfolge egal). */
export function suchWoerter(suche: string): string[] {
    return (suche ?? "")
        .toLowerCase()
        .split(/\s+/)
        .map((w) => w.trim())
        .filter(Boolean);
}

export function passtZurSuche(haystack: string, woerter: string[]): boolean {
    for (const w of woerter) if (!haystack.includes(w)) return false;
    return true;
}

/** Filtert über Betreff, Absender, Empfänger (An/Cc), Vorschau, Vorgang, Ordner. */
export function filtereKommunikation(zeilen: KommMailZeile[], suche: string): KommMailZeile[] {
    const woerter = suchWoerter(suche);
    if (woerter.length === 0) return zeilen;
    return zeilen.filter((z) => passtZurSuche(suchtext(z), woerter));
}

// ── Sortierung ──────────────────────────────────────────────────────────────

export type KommSortFeld = "datum" | "richtung" | "von" | "an" | "betreff" | "ordner" | "anhaenge" | "vorgang";
export type KommSort = { field: string; direction: "asc" | "desc" } | null;

const vergleichText = new Intl.Collator("de", { sensitivity: "base", numeric: true }).compare;

const sortWert = (z: KommMailZeile, feld: string): string | number => {
    switch (feld as KommSortFeld) {
        case "richtung":
            return z.richtung;
        case "von":
            return z.von;
        case "an":
            return z.an;
        case "betreff":
            return z.email.subject ?? "";
        case "ordner":
            return `${z.mailbox} ${z.ordner ?? ""}`;
        case "anhaenge":
            return anhangAnzahl(z) ?? 1;
        case "vorgang":
            return z.vorgang?.titel ?? "";
        default:
            return z.zeit;
    }
};

/**
 * Stabile Sortierung; ohne Sortierung (oder unbekanntes Feld) neueste zuerst.
 * Leere Texte stehen immer hinten — „ohne Vorgang" soll beim Sortieren nach
 * Vorgang nicht die ersten Seiten füllen.
 */
export function sortiereKommunikation(zeilen: KommMailZeile[], sort: KommSort): KommMailZeile[] {
    const feld = sort?.field ?? "datum";
    const richtung = sort?.direction === "asc" ? 1 : -1;
    return zeilen
        .map((z, i) => ({ z, i }))
        .sort((a, b) => {
            const va = sortWert(a.z, feld);
            const vb = sortWert(b.z, feld);
            let c: number;
            if (typeof va === "number" && typeof vb === "number") {
                c = (va - vb) * richtung;
            } else {
                const sa = String(va);
                const sb = String(vb);
                if (!sa && sb) return 1;
                if (sa && !sb) return -1;
                c = vergleichText(sa, sb) * richtung;
            }
            if (c === 0) c = b.z.zeit - a.z.zeit;
            return c !== 0 ? c : a.i - b.i;
        })
        .map(({ z }) => z);
}

/** Datum + Uhrzeit (Europe/Berlin) für die Tabellenzelle. */
export function formatZeit(zeit: number): { datum: string; uhrzeit: string } {
    if (!zeit) return { datum: "—", uhrzeit: "" };
    const d = new Date(zeit);
    return {
        datum: d.toLocaleDateString("de-DE", { timeZone: "Europe/Berlin", day: "2-digit", month: "2-digit", year: "numeric" }),
        uhrzeit: d.toLocaleTimeString("de-DE", { timeZone: "Europe/Berlin", hour: "2-digit", minute: "2-digit", hour12: false }),
    };
}

// ── Spaltenfilter ───────────────────────────────────────────────────────────
//
// Die Filter-Lupen im Tabellenkopf (DataTable-Engine). Gefiltert wird im
// Browser über den geladenen Bestand — derselbe, den Suche und Sortierung
// sehen. Werte sind die Anzeige-Texte der Dropdown-Optionen.

export const RICHTUNG_OPTIONEN = ["Eingang", "Ausgang"] as const;
export const ANHANG_MIT = "Mit Anhängen";
export const ANHANG_OHNE = "Ohne Anhänge";
export const VORGANG_MIT = "Mit Vorgang";
export const VORGANG_OHNE = "Ohne Vorgang";

/** „Postfach · Ordner" — Anzeige und Filterwert der Spalte Postfach/Ordner. */
export function ordnerLabel(z: Pick<KommMailZeile, "mailbox" | "ordner" | "link">): string {
    const postfach = z.mailbox ? mailboxChipLabel(z.mailbox) : "—";
    const ordner = z.ordner ?? (z.link ? "Zugewiesen" : "");
    return ordner ? `${postfach} · ${ordner}` : postfach;
}

/**
 * Anzahl der Anhänge, soweit bekannt: aus der Sicherung (Verknüpfung) exakt,
 * aus Graph nur „hat Anhänge" → `null` = ja, Zahl unbekannt; 0 = keine.
 */
export function anhangAnzahl(z: Pick<KommMailZeile, "email" | "link">): number | null {
    const gesichert = z.link?.archive?.anhaenge;
    if (typeof gesichert === "number" && gesichert > 0) return gesichert;
    return z.email.hasAttachments ? null : 0;
}

export const hatAnhaenge = (z: Pick<KommMailZeile, "email" | "link">): boolean => anhangAnzahl(z) !== 0;

export type KommMailFilterWerte = Record<string, string | boolean | undefined>;

/** Dropdown-Optionen, die vom Bestand abhängen (Ordner, Vorgänge). */
export function mailFilterOptionen(zeilen: KommMailZeile[]): { ordner: string[]; vorgang: string[] } {
    const ordner = new Set<string>();
    const vorgaenge = new Set<string>();
    for (const z of zeilen) {
        ordner.add(ordnerLabel(z));
        if (z.vorgang?.titel) vorgaenge.add(z.vorgang.titel);
    }
    const sortiere = (xs: Iterable<string>) => Array.from(xs).sort(vergleichText);
    return { ordner: sortiere(ordner), vorgang: [VORGANG_MIT, VORGANG_OHNE, ...sortiere(vorgaenge)] };
}

const textPasst = (wert: string | undefined | null, filter: string): boolean =>
    passtZurSuche((wert ?? "").toLowerCase(), suchWoerter(filter));

/** Wendet die Spaltenfilter der Mail-Tabelle an (alle aktiven müssen passen). */
export function filtereMailSpalten(
    zeilen: KommMailZeile[],
    werte: KommMailFilterWerte,
    datum?: DateRangeValue,
): KommMailZeile[] {
    const w = (id: string) => {
        const v = werte?.[id];
        return typeof v === "string" && v.trim() ? v : undefined;
    };
    const richtung = w("richtung");
    const von = w("von");
    const an = w("an");
    const betreff = w("betreff");
    const anhaenge = w("anhaenge");
    const ordner = w("ordner");
    const vorgang = w("vorgang");
    return zeilen.filter((z) => {
        if (!zeitImDatumsbereich(z.zeit, datum)) return false;
        if (richtung && (richtung === "Eingang" ? z.richtung !== "eingang" : z.richtung !== "ausgang")) return false;
        if (von && !textPasst(`${z.von} ${z.email.senderEmail ?? ""}`, von)) return false;
        if (an && !textPasst(z.an, an)) return false;
        if (betreff && !textPasst(`${z.email.subject ?? ""} ${z.email.preview ?? ""}`, betreff)) return false;
        if (anhaenge === ANHANG_MIT && !hatAnhaenge(z)) return false;
        if (anhaenge === ANHANG_OHNE && hatAnhaenge(z)) return false;
        if (ordner && ordnerLabel(z) !== ordner) return false;
        if (vorgang === VORGANG_MIT && !z.vorgang) return false;
        if (vorgang === VORGANG_OHNE && z.vorgang) return false;
        if (vorgang && vorgang !== VORGANG_MIT && vorgang !== VORGANG_OHNE && z.vorgang?.titel !== vorgang) return false;
        return true;
    });
}

/** Wie viele Spaltenfilter sind gesetzt? (Hinweis „gefiltert" + Zurücksetzen) */
export function aktiveFilterAnzahl(werte: KommMailFilterWerte, datum?: DateRangeValue): number {
    let n = 0;
    for (const v of Object.values(werte ?? {})) if (v !== undefined && v !== "" && v !== false) n++;
    if (datum && (datum.von || datum.bis)) n++;
    return n;
}
